import CourseSettingsService from 'services/course-handler-services/course-settings-service';
import LocaleService from 'services/loader-handler-services/locale-service';
import yaml from 'js-yaml';
import EF from 'ef/ef';
import _ from 'lodash';
import utils from 'lib/utils';

/*
 * CustomPageService provides a mechanism for injecting or replacing pages via a customization.
 *
 * The customization response can replace pages by providing a response like this:
 *   {
 *     "type": "page-content-customization",
 *     "customizations_map": {
 *       "some-module": {
 *         "some-activity": {
 *           "some-page": "https://everfi.github.io/course-customizations/customizations/Skeletor/example-page"
 *         }
 *       }
 *     }
 *   }
 *
 * The customization response can inject pages before or after a certain page key like this:
 *   {
 *     "type": "page-content-customization",
 *     "customizations_map": {
 *       "some-module": {
 *         "some-activity": {
 *           "injected-before": {
 *             "url": "https://everfi.github.io/course-customizations/customizations/Skeletor/example-page",
 *             "before": "some-page-key"
 *           },
 *           "injected-after": {
 *             "url": "https://everfi.github.io/course-customizations/customizations/Skeletor/example-page",
 *             "after": "some-page-key"
 *           }
 *         }
 *       }
 *     }
 *   }
 *
 * The customization response can pull in a set of blocks to a page like this:
 *   {
 *     "type": "page-content-customization",
 *     "customizations_map": {
 *       "some-module": {
 *         "some-activity": {
 *           "some-page": {
 *             "some-slug": "https://everfi.github.io/course-customizations/customizations/Skeletor/example-page"
 *           }
 *         }
 *       }
 *     }
 *   }
 *
 *  Then `some-page` and only `some-page` can use the custom-page block like this
 *    - type: custom-page
 *      slug: some-slug
 *  Note that the page could use other blocks in conjunction with this block, including another custom-page block
 *
 * The customization response can also skip pages with a null in the customizations_map
 *   {
 *     "type": "page-content-customization",
 *     "customizations_map": {
 *       "some-module": {
 *         "some-activity": {
 *           "some-skipped-page": null
 *         }
 *       }
 *     }
 *   }
 *
 *  The URL in those examples is the base url for a file called metadata.json which describes the other files to fetch
 *    {
 *      "locales": [ "en", "es" ],
 *      "format": "yaml"
 *    }
 *  Will fetch content.yml, locales/en.json, and locales/es.json
 *
 *
 *  Note About Loading page-n.js Files
 *  ==================================
 *
 *  Custom pages currently do not include the page-n.js script file. To include these, they can be pushed into
 *  the pageQueue like all of the other resources for each page. Then call `response.text()` and eval it
 *  with the common js wrapper inline. It'll look something like this for the current namespace:
 *  ```
 *  fetch(`url-to-script-file`).then(r => r.text()).then(scriptBody => {
 *     eval(`
 *      window.require.register('modules/${EF.module.currentActivity.currentPage.get('moduleKey')}/${EF.module.currentActivity.currentPage.get('activityKey')}/${EF.module.currentActivity.currentPage.get('key')}/${EF.module.currentActivity.currentPage.get('key')}', function(exports, require, module) {
 *      ${scriptBody}
 *    });
 *  `);
 *  });
 *  ```
 *  eval is very dangerous, if this is implemented, make sure the script comes from a trusted source
 *  the risk for XSS is very high here since this more or less intentionally injects a raw
 *  script file on the fly. Double, triple check the security of such a feature before moving forward with it
 */
export default class CustomPageService {
  /*
   * Loads a single page on the fly after everything is initialized.
   * The localizations are added to the current LocaleService namespace.
   * Used by app/views/block/custom-page/custom-page-view.js
   *
   * @returns {Promise} Promise object where the result has the blocks for the page and the locales
   * @example
   * CustomPageService.loadPage('some-slug').then(({blocks, locales}) => {
   *   // blocks is an array of renderable blocks
   *   // locales is an object like this:
   *   //  { en: { someKey: 'some translation', es: { someKey: 'Spanish translation' } }}
   * });
   */
  static loadPage(slug, module, activity, page) {
    if (!CustomPageService.hasPageCustomization(slug, module, activity, page)) {
      return CustomPageService.emptyValue();
    }

    const url = CustomPageService.customizationUrl(slug, module, activity, page);

    return fetch(`${url}/metadata.json`)
      .then(r => r.json())
      .then(metadata => {
        const queue = [];

        if (['yaml', 'yml', 'json'].includes(metadata.format)) {
          metadata.format = metadata.format === 'yaml' ? 'yml' : metadata.format;
          queue.push(
            fetch(`${url}/content.${metadata.format}`).then(r => {
              return r.text().then(data => {
                return Promise.resolve(metadata.format === 'json' ? JSON.parse(data) : yaml.load(data));
              });
            })
          );
        }

        if (metadata.locales && Array.isArray(metadata.locales)) {
          queue.push(
            ...metadata.locales.map(locale => {
              return CustomPageService.getLocale(locale, url);
            })
          );
        }

        return Promise.all(queue).then(([page, ...locales]) => {
          return {
            blocks: page.blocks,
            locales: locales.reduce((result, locale) => {
              Object.assign(result, locale);
              return result;
            }, {}),
          };
        });
      });
  }

  static hasPageCustomization(slug, module, activity, page) {
    if (!slug) {
      return false;
    }
    return CustomPageService.customizationUrl(slug, module, activity, page) !== null;
  }

  static customizationUrl(slug, module, activity, page) {
    const customization = EF.customizations.findByType('page-content-customization');
    if (customization && customization.has('customizations_map')) {
      const map = customization.get('customizations_map');
      const url = [module, activity, page, slug].reduce(
        (result, key) => (result !== null && Object.prototype.hasOwnProperty.call(result, key) ? result[key] : null),
        map
      );
      return url !== null ? url.replace(/{{courseUrl}}/, CourseSettingsService.get('courseUrl')) : null;
    }
    return null;
  }

  static modulePaths() {
    return {
      module: EF.module.get('key'),
      activity: EF.module.currentActivity.get('key'),
      page: EF.module.currentActivity.currentPage.get('key'),
    };
  }

  static emptyValue() {
    console.warn(
      'CustomPageService: could not resolve a customization. Check that the slug is correct and the customization exists'
    ); //eslint-disable-line no-console
    return new Promise(resolve => resolve({ blocks: [], locales: [] }));
  }

  /*
   * This is responsible for injecting the page metadata into the module structure object before initialization
   *
   * @param {Array} modules - Raw serialized json from before initialization. This will be mutated in place.
   * @param {Object} info - Contains before and after page keys to figure out where to inject the page
   */
  static injectPageMetaData(modules, info, moduleKey, activityKey, pageKey, pageTitle) {
    const module = modules.find(m => m.key === moduleKey);
    if (module) {
      const activity = module.activities.find(a => a.key === activityKey);
      if (activity && activity.pages && Array.isArray(activity.pages)) {
        // sort pages by the index property
        activity.pages.sort(CustomPageService.compareByIndex);

        const injectedPage = {
          title: pageTitle,
          key: pageKey,
          script: false,
          custom: true, // one way of marking injected pages
        };

        // inject the page before or after the page key
        const beforeIndex = activity.pages.findIndex(page => page.key === info.before);
        const afterIndex = activity.pages.findIndex(page => page.key === info.after);
        if (beforeIndex >= 0) {
          activity.pages.splice(beforeIndex, 0, injectedPage);
        } else if (afterIndex >= 0) {
          injectedPage.index = afterIndex + 1;
          activity.pages.splice(afterIndex + 1, 0, injectedPage);
        }

        // since it was sorted by index and then the pages were injected, this normalizes all of the
        // page indices and makes them sequential
        activity.pages.forEach((page, index) => {
          page.index = index;
        });
      }
    }
  }

  static orderPagesByIndexReducer(result, module) {
    if (Array.isArray(module.activities)) {
      // sort all of the activities and pages by the index property, so that later the
      // next page index can be determined even if the index is misordered
      module.activities.sort(CustomPageService.compareByIndex);
      module.activities.forEach(activity => {
        if (Array.isArray(activity.pages)) {
          activity.pages = CustomPageService.markLastPageOfModuleActivity(activity.pages);
          result = result.concat(activity.pages);
        }
      });
    }
    return result;
  }

  static compareByIndex(obj1, obj2) {
    return obj1.index < obj2.index ? -1 : 1;
  }

  static ReplacePageFromURLWithMetadataAsPageQueue(url, info, modules, moduleKey, activityKey, pageKey, menu = false) {
    const pageQueue = [];
    return fetch(`${url}/metadata.json`)
      .then(response => response.json())
      .then(metadata => {
        // a sub queue for all of the files for each page, each promise will resolve when
        // each part is loaded like content.yml and each of the locale files
        if (['yaml', 'yml', 'json'].includes(metadata.format)) {
          metadata.format = metadata.format === 'yaml' ? 'yml' : metadata.format;
          pageQueue.push(
            fetch(`${url}/content.${metadata.format}`)
              .then(r => r.text())
              .then(data => {
                const content = metadata.format === 'json' ? JSON.parse(data) : yaml.load(data);
                const mergable = !menu
                  ? {
                      [moduleKey]: {
                        [activityKey]: {
                          [pageKey]: content,
                        },
                      },
                    }
                  : {
                      [moduleKey]: {
                        [activityKey]: content,
                      },
                    };
                if (info.after || info.before) {
                  // this is adding a new page in the module
                  CustomPageService.injectPageMetaData(
                    modules,
                    info,
                    moduleKey,
                    activityKey,
                    pageKey,
                    metadata.title || ''
                  );
                  return Promise.resolve(mergable);
                } else {
                  //this is a page replacement in the module
                  return new Promise(resolve => {
                    resolve((modules, moduleContentRef) => {
                      if (!menu) {
                        CustomPageService.replacePage(moduleContentRef, mergable, moduleKey, activityKey, pageKey);
                      } else {
                        CustomPageService.replaceMenu(moduleContentRef, mergable, activityKey);
                      }
                      return mergable;
                    });
                  });
                }
              })
          );
        }
        if (metadata.locales && Array.isArray(metadata.locales)) {
          pageQueue.push(
            ...metadata.locales.map(locale => {
              return CustomPageService.getLocale(locale, url);
            })
          );
        }
        return Promise.all(pageQueue);
      });
  }

  /*
   * This is responsible for loading n pages and normalizing the data so that it can be merged into
   * the raw JSON before initialization
   *
   * @param {Array}  customizations_map - Array of all of the customizations with their urls
   * @param {Object} modules      - The module structure object from before course initialization.
   *                                This will be mutated in place to add the injected and replaced pages
   */
  static loadPages(customizations_map, modules) {
    // The main queue for each page, each promise will resolve when the page is fully loaded
    const queue = [];
    Object.entries(customizations_map).forEach(([moduleKey, activities]) => {
      Object.entries(activities).forEach(([activityKey, pages], activityIndex) => {
        const isLastActivity = Object.entries(activities).length - 1 === activityIndex;
        Object.entries(pages).forEach(([pageKey, info], pageIndex) => {
          if (info === null || info === undefined || info === '') {
            // null, undefined, or empty string means the page is to be skipped, it will be removed before init
            queue.push(
              Promise.all([
                new Promise(resolve => {
                  return resolve((modulesRef, moduleContentRef) => {
                    // modulesRef and moduleContentRef are the references to the final module and module content objects
                    // they are here so that they can be mutated in place, in this case we found a null replacement
                    // which needs to be removed
                    const moduleRef = modules.find(m => m.key === moduleKey);
                    if (moduleRef && Array.isArray(moduleRef.activities)) {
                      const activityRef = moduleRef.activities.find(a => a.key === activityKey);
                      if (activityRef && activityRef.pages) {
                        activityRef.pages.sort(CustomPageService.compareByIndex);
                        const activityRefPageIndex = activityRef.pages.findIndex(p => p.key === pageKey);

                        activityRef.pages = activityRef.pages.filter((elem, i) => i !== activityRefPageIndex);

                        moduleContentRef[moduleKey][activityKey] = Object.keys(
                          moduleContentRef[moduleKey][activityKey]
                        ).reduce((object, key) => {
                          if (key !== pageKey) {
                            object[key] = moduleContentRef[moduleKey][activityKey][key];
                          }
                          return object;
                        }, {});
                      }
                    }
                    // set skippedLastPage on the moduleRef
                    const isLastPage = Object.entries(pages).length - 1 === pageIndex;
                    if (isLastPage && isLastActivity) {
                      const foundModule = modulesRef.find(module => module.key === moduleKey);
                      foundModule.skippedLastPage = {
                        module_key: moduleKey,
                        activity_key: activityKey,
                        key: pageKey,
                        page_key: pageKey,
                        index: pageIndex,
                      };
                    }
                  });
                }),
              ])
            );
          } else if (Object.keys(info).includes('url')) {
            const url = typeof info === 'string' ? info : info.url;
            queue.push(
              CustomPageService.ReplacePageFromURLWithMetadataAsPageQueue(
                url,
                info,
                modules,
                moduleKey,
                activityKey,
                pageKey,
                false
              )
            );
          } else if (typeof info === 'string') {
            // if `info' is only a string, it is a menu override
            const url = info.endsWith('/') ? info.substring(0, info.length - 1) : info;
            queue.push(
              CustomPageService.ReplacePageFromURLWithMetadataAsPageQueue(
                url,
                info,
                modules,
                moduleKey,
                activityKey,
                pageKey,
                true
              )
            );
          }
        });
      });
    });
    return Promise.all(queue);
  }

  static getLocale(locale, url) {
    return fetch(`${url}/locales/${locale}.json`)
      .then(r => r.json())
      .then(data => {
        return Promise.resolve({ [locale]: data });
      });
  }

  static replacePage(moduleContentRef, mergable, moduleKey, activityKey, pageKey) {
    if (moduleContentRef[moduleKey][activityKey][pageKey]) {
      moduleContentRef[moduleKey][activityKey][pageKey] = mergable[moduleKey][activityKey][pageKey];
    }
  }

  static replaceMenu(moduleContentRef, mergable, menuKey) {
    if (moduleContentRef['menu'][menuKey]) {
      moduleContentRef['menu'][menuKey] = mergable['menu'][menuKey];
    }
  }

  /*
   * This is the entry point for injecting pages before initialization.
   * Note: it mutates the parameters
   *
   * @param {Object} moduleContent - the raw serialized json for the module including all
   *                                 of the blocks for each page
   * @param {Object} modules -  the raw serialized json containing only the structure of
   *                            the module without the blocks for each page
   */
  static replaceCustomPages(modules, moduleContent, groupsJSON) {
    const customizations = EF.customizations.findByType('page-content-customization');
    if (customizations && customizations.has('customizations_map')) {
      const customizations_map = customizations.get('customizations_map');
      return CustomPageService.loadPages(customizations_map, modules).then(bundles => {
        bundles.forEach(([mergable, ...locales]) => {
          // mergable is a full object with the module, activity, and page keys like this:
          // {
          //   someModule: { someActivity: { somePage: { blocks: [ ... ] } } }
          // }
          // It may also be a function, which means a null replacement was found
          if (typeof mergable === 'function') {
            // found a callback which means the page has been removed or replaced, call it
            // and let the callback mutate modules and moduleContent to remove the page or replace the blocks
            // if it's a replacement, will also return the mergable page data so locales are added correctly below
            mergable = mergable(modules, moduleContent);
          } else if (mergable) {
            _.merge(moduleContent, mergable);
          }

          // traverse down to each page and inject the locales with the right namespace
          if (mergable && locales && Array.isArray(locales)) {
            Object.entries(mergable).forEach(([moduleKey, activities]) => {
              Object.entries(activities).forEach(([activityKey, pages]) => {
                Object.entries(pages).forEach(([pageKey]) => {
                  locales.forEach(locale => {
                    if (locale) {
                      Object.entries(locale).forEach(([code, data]) => {
                        LocaleService.addResource(code, `${moduleKey}.${activityKey}.${pageKey}`, {
                          [moduleKey]: {
                            [activityKey]: {
                              [pageKey]: data,
                            },
                          },
                        });
                      });
                    }
                  });
                });
              });
            });
          }
        });
        return Promise.resolve([modules, moduleContent, groupsJSON]);
      });
    } else {
      return new Promise(resolve => resolve([modules, moduleContent, groupsJSON]));
    }
  }

  static markLastPageOfModuleActivity(pages) {
    const orderedPages = CustomPageService.orderPagesByIndex(pages);
    if (orderedPages.length > 0) {
      utils.getLastArrayElement(orderedPages).lastPageOfModule = true;
    }
    return orderedPages;
  }

  static orderPagesByIndex(pages) {
    return pages
      .filter(p => p !== undefined)
      .sort((currentPage, nextPage) => (currentPage.index < nextPage.index ? -1 : 1));
  }
}
